import { Injectable } from '@angular/core';
import { FilesProvider } from '../../files/providers/files-provider';
import { CurrentUserRolesProvider } from '../../organizations/providers/current-user-roles-provider';
import { ExerciceItemRightsList, ExerciceRights, mapExerciceItemRightsToExerciceRights } from './exercice-rights';
import { catchError, map, switchMap } from 'rxjs/operators';
import { forkJoin, from, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ExerciceCategory } from "../../common/models/exercice-category";
import { ConfigProvider } from "../../config-provider.service";
import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { ApiBaseService } from "../../common/providers/api-base-service";
import { LangsProvider } from "../../common/providers/langs-provider";
import { CurrentUser } from "../../common/providers/current-user";
import { Exercice, ExerciceData } from "../../common/models/exercice";
import { Paginator } from "../../common/models/paginator";
import { User } from "../../common/models/user";
import { Organization } from "../../common/models/organization";
import { AnnualPlan } from "../../common/models/annual-plan";
import { Week } from "../../common/models/week";
import { UsersProvider } from "../../users/providers/users-provider";
import { Library } from '../../common/models/library';

@Injectable({providedIn: 'root'})
export class ExercicesProvider {
  public Ready: Promise<boolean> = new Promise((resolve, reject) => resolve(false));
  private availableExerciceCategories: ExerciceCategory[] = [];


  constructor(
    private _conf: ConfigProvider,
    private _utils: UtilsProvider,
    private api: ApiBaseService,
    private usersProvider: UsersProvider,
    private _langs: LangsProvider,
    private _currentUserRoles: CurrentUserRolesProvider,
    private _currentUser: CurrentUser,
    private _files: FilesProvider
  ) {

    this.Ready = new Promise((resolve, reject) => {
      this.getExerciceCategories().subscribe({
        next: (exerciceCategories) => {
          this.availableExerciceCategories = exerciceCategories;
          resolve(true);
        },
        error: reject
      });
    });

  }

  public getExercice(id:any): Observable<Exercice> {
    return this.api.call('get', 'exercices/' + id, {}, this._currentUser.getAccessToken()).pipe(
      map((data: Exercice) => Exercice.fromAPI(data))
    );
  }

  public getLibraryById(organizationId: number, id: number): Observable<Library> {
    return this.api.call('get', `organizations/${organizationId}/libraries/${id}`, {}, this._currentUser.getAccessToken()).pipe(
      map((data: Library) => Library.fromAPI(data, []))
    );
  }

  public getLibraries(organizationId: number): Observable<Library[]> {
    return this.api.call('get', `organizations/${organizationId}/libraries`, {}, this._currentUser.getAccessToken()).pipe(
      map((data: Library[]) => Library.fromAPIList(data, []))
    );
  }

  public getExercicesByLibraryId(libraryIds: number[], pageNumber = 1, itemsPerPage: number = environment.itemsPerPage, search = '', selectedCategoryIds: number[] = [], order: exerciceOrderByOptions = exerciceOrderByOptions.created_desc, filter?: string | null): Observable<Paginator> {
    let stringParams = `page=${pageNumber}&per_page=${itemsPerPage}&keyword=${search}&sort=${order}&filter-key=${filter}`;
    selectedCategoryIds.forEach((categoryId) => {
      stringParams = stringParams.concat(`&categories[]=${categoryId}`);
    });

    return this.api.call('get',`exercices/by-library/${JSON.stringify(libraryIds)}?${stringParams}`, {}, this._currentUser.getAccessToken());
  }


  public getExercisesByLibraryIdAndUser(libraryIds: number[], pageNumber = 1, itemsPerPage: number = environment.itemsPerPage, search = '', selectedCategoryIds: number[] = [], order: exerciceOrderByOptions = exerciceOrderByOptions.created_desc): Observable<Paginator> {
    let stringParams = `page=${pageNumber}&per_page=${itemsPerPage}&keyword=${search}&sort=${order}`;
    selectedCategoryIds.forEach((categoryId) => {
      stringParams = stringParams.concat(`&categories[]=${categoryId}`);
    });
    return this.api.call('get',`exercices/by-library/${JSON.stringify(libraryIds)}/user?${stringParams}`, {}, this._currentUser.getAccessToken());
  }

  public findOneById(id: number): Observable<Exercice> {
    return this.getExercice(id).pipe(
      map((exercice: Exercice) => exercice)
    );
  }

  public getFutureByLibraryId(libraryId: number, fromDate: string) {
    return this.api.call('get', 'exercices/by-library/' + libraryId + '/future/' + fromDate, {}, this._currentUser.getAccessToken());
  }

  public getExerciceRows(organization: Organization, libraryIds: number | number[], fetchImage = true, fetchVideo = true, addRights = true, page?:number, itemsPerPage?: number, search?:string, selectedCategoryIds?: number[]|undefined, order: exerciceOrderByOptions = exerciceOrderByOptions.name_asc, filter?: string | null): Observable<Paginator> {
    const _libraryIds: number[] = typeof libraryIds === 'number' ? [libraryIds] : libraryIds;
    return this.getExercicesByLibraryId(_libraryIds, page, itemsPerPage, search, selectedCategoryIds, order, filter);
  }

  public getExerciseRowsByUser(organization: Organization, libraryId: number | number[], fetchImage = true, fetchVideo = true, addRights = true, page?:number, itemsPerPage?: number, search?:string, selectedCategoryIds?: number[]|undefined, order: exerciceOrderByOptions = exerciceOrderByOptions.name_asc): Observable<Paginator> {
    const _libraryIds: number[] = typeof libraryId === 'number' ? [libraryId] : libraryId;

    return this.getExercisesByLibraryIdAndUser(_libraryIds, page, itemsPerPage, search, selectedCategoryIds, order);
  }

  public getExerciceRow(organization: Organization, exerciceId: number, fetchImage = true, fetchVideo = true, addrights = true): Observable<ExerciceRow> {
    return this.getExercice(exerciceId).pipe(
      switchMap((exercice: ExerciceData) => this.e_toExerciceRow(organization, Exercice.fromAPI(exercice), fetchImage, fetchVideo, addrights))
    );
  }

  public addImageToRow(organizationId: number, row: ExerciceRow) {
    return null;
  }

  public getExerciceCategories(): Observable<ExerciceCategory[]> {
    return this.api.call('get', 'nosync/common-data/exercise-categories', {}, this._currentUser.getAccessToken()).pipe(
      map((categories) => ExerciceCategory.fromAPIList(categories))
    );
  }

  public getExerciseCategories(organization: Organization): Observable<ExerciceCategory[]> {
    return this.api.call('get',`organizations/${organization.code}/plans/categories`, {}, this._currentUser.getAccessToken()).pipe(
      map((categories) =>  ExerciceCategory.fromAPIList(categories))
    );
  }

  public saveExercice(organizationId: number, exercice: Exercice, selectedLangs: any, ignore_video: boolean): Observable<Exercice> {
    return this.api.call('post', 'organizations/' + organizationId + '/exercices/' + (exercice.id ?? 0),
      this._utils.buildExerciceParams([exercice], selectedLangs, ignore_video)[0],
      this._currentUser.getAccessToken(), false
    ).pipe(
      map((exercice: ExerciceData) => {
        return Exercice.fromAPI(exercice);
      })
    );
  }

  public deleteExercice(organizationId: number, exercice: Exercice): Observable<any> {
    return this.api.call(                'delete',
      'organizations/' + organizationId + '/exercices/' + exercice.id,
      {},
      this._currentUser.getAccessToken()
    );
  }

  public getImageFileByOrganizationIdAndExerciseId(organizationId: number, exerciseId: number, returnType = 'dataUri', noCache = true): Observable<any> {
    const noCacheHack: string = noCache ? '?' + new Date().getTime() : '';
    if (returnType === 'dataUri') {
      return this.getExercice(exerciseId).pipe(
        map((exercice: Exercice) => this._conf.storageRootUrl + exercice.image + noCacheHack),
      );
      return of();
    }

    return this._files.downloadFile('organizations/' + organizationId + '/exercices/' + exerciseId + '/download-image' + noCacheHack).pipe(
      map((fileData: any) => {
        const reader: FileReader = new FileReader();
        reader.onload = () => {
          this._utils.dataURItoFile(reader.result as string).then(r => r).catch(() => null);
        };
        reader.readAsDataURL(fileData);
      }),
    );
  }

  public getImageFile(organizationId: number, row: ExerciceRow, returnType = 'dataUri', noCache = true): Observable<any> {
    if (!row.exercice.image) {
      return of(null)
    }

    const noCacheHack: string = noCache ? '?' + new Date().getTime() : '';
    if (returnType === 'dataUri') {
      return of(this._conf.storageRootUrl + row.exercice.image + noCacheHack);
    }

    return this._files.downloadFile('organizations/' + organizationId + '/exercices/' + row.exercice.id + '/download-image' + noCacheHack)
      .pipe(
        switchMap((fileData: any) => {
          if (returnType === 'blob') {
            return of(fileData);
          } else {
            return new Observable(observer => {
              const reader: FileReader = new FileReader();
              reader.onload = () => {
                observer.next(reader.result);
                observer.complete();
              };
              reader.onerror = () => {
                observer.error('FileReader failed');
              };
              reader.readAsDataURL(fileData);
            }).pipe(
              switchMap((dataUrl: any) => from(this._utils.dataURItoFile(dataUrl)))
            );
          }
        }),
        catchError(() => {
          return of(null);
        })
      );
  }

  public getVideoFile(organizationId: number, row: ExerciceRow, returnType = 'dataUri'): Observable<any> {
    if (!row.exercice.video_path) {
      return of(null)
    }

    if (returnType === 'dataUri') {
      return of(this._conf.storageRootUrl + row.exercice.video_path);
    }

    return this._files.downloadFile('organizations/' + organizationId + '/exercices/' + row.exercice.id + '/download-video', null, true)
      .pipe(
        switchMap((fileData: any) => {
          if (returnType === 'blob') {
            return of(fileData);
          } else {
            return new Observable(observer => {
              const reader: FileReader = new FileReader();
              reader.onload = () => {
                observer.next(reader.result);
                observer.complete();
              };
              reader.onerror = () => {
                observer.error('FileReader failed');
              };
              reader.readAsDataURL(fileData);
            }).pipe(
              switchMap((dataUrl: any) => from(this._utils.dataURItoFile(dataUrl)))
            );
          }
        }),
        catchError(() => {
          return of(null);
        })
      );
  }

  public getTotalTime(row: ExerciceRow): number {

    const toReturn: number = row.exercice.duration??0;

    return toReturn;

  }

  public findByAnnualPlan(annualPlan: AnnualPlan): Observable<ExerciceCategory[]> {
    return this.api.call('get', 'annual-plans/' + annualPlan.id + '/exercices/categories',{}, this._currentUser.getAccessToken()).pipe(
      map((data: any) => data.exerciceCategories.map((exercice: ExerciceCategory) => ExerciceCategory.fromAPI(exercice)))
    );
  }

  public findByWeek(week: Week): Observable<ExerciceCategory[]> {
    return this.api.call('get', 'weeks/' + week.id + '/exercices/categories', {}, this._currentUser.getAccessToken()).pipe(
      map((data: any) => data.exerciceCategories.map((exercice: ExerciceCategory) => ExerciceCategory.fromAPI(exercice)))
    );
  }

  public e_toExerciceRow(
    organization: Organization,
    exercice: Exercice,
    fetchImage = true,
    fetchVideo = true,
    addRights = true,
  ): Observable<ExerciceRow> {
    const toReturn: ExerciceRow = {
      exercice,
      categories: [
        this._utils.findIn(exercice.accent1, this.availableExerciceCategories, 'id'),
        this._utils.findIn(exercice.accent2, this.availableExerciceCategories, 'id'),
        this._utils.findIn(exercice.accent3, this.availableExerciceCategories, 'id')
      ],
      exerciceImage: null,
      exerciceVideo: null,
      creater: exercice.creater ?? null,
      rights: undefined,
      libraryTag: exercice.libraryTag,
    };

    const toFetch: Array<Observable<any>> = [];
    if (!organization.id) throw new Error('Organization not set');
    toFetch.push(fetchImage ? this.getImageFile(organization.id, toReturn) : of(null));
    toFetch.push(fetchVideo ? this.getVideoFile(organization.id, toReturn) : of(null));

    return forkJoin(toFetch).pipe(
      map(([image, video]) => {
        toReturn.exerciceImage = image;
        toReturn.exerciceVideo = video;

        if (addRights) {
          toReturn.rights = mapExerciceItemRightsToExerciceRights(exercice.rights);
        }

        return toReturn;
      }),
    );
  }
}

export interface ExerciceRow {
  exercice: Exercice;
  exerciceImage: any;
  exerciceVideo: any;
  categories: ExerciceCategory[];
  creater: User | null;
  rights?: ExerciceItemRightsList;
  libraryTag?: string;
}

// sorting option should be 'duration_asc'|'name_asc'|'created_desc'
export enum exerciceOrderByOptions {
  duration_asc = 'duration_asc',
  name_asc = 'name_asc',
  created_desc = 'created_desc',
}

